import { fetchApi, withCluster } from '@app/hooks/api';
import { apiCheckJob } from '@app/GranularDeploy/helpers/apiHelpers';
import { ParamFieldSchema } from '../../../../shared/previewEnv/regressionTestParamSchema';

export { apiCheckJob };

/** A curated real topic/group-id from a service's own mirrord-preview.tmpl (DEVEX-819). */
export interface KafkaTopicCatalogEntry {
  id: string;
  topic_env_var?: string;
  topic_fallback?: string;
  group_id_env_var?: string;
  group_id_fallback?: string;
}

export interface OverlayCandidate {
  application: string;
  branch: string;
  exists: boolean;
  workloadType?: 'deployment' | 'statefulset';
  isRunning?: boolean;
  envVarNames?: string[];
  /** Values of DATABASE/DB_NAME/DBNAME/DB/SCHEMA-suffixed env vars, captured live from the running
   *  pod — keyed by env var name, used to prefill the DB Branching "Database Name" field. */
  dbSchemaNames?: Record<string, string>;
  /** appmetadata.metadata.db_branches for this service, if any — see DbBranchAppMetaOverride. */
  dbBranchOverrides?: Partial<Record<DbBranchUiType, DbBranchAppMetaOverride>>;
  /** Pre-populated Kafka topic catalog for this service, if any — see KafkaTopicCatalogEntry. */
  kafkaTopics?: KafkaTopicCatalogEntry[];
  /** Hardcoded env var defaults from mirrord-preview.tmpl's feature.default_env (e.g. health-info's
   *  RAILS_ENV=staging) — auto-prefills the manual env override section. */
  defaultEnv?: Record<string, string>;
  /** Companion deployments (e.g. tas-sidekiq, tas-karafka) that run this service's same image as
   *  a separate Deployment — selecting this candidate should default-select these too. */
  companions?: string[];
  behindInfo?: { application: string; branch: string; behind_by: number } | null;
  images?: {
    image_name?: string;
    deploy_tags?: string[];
    commits?: string[];
    found_tags?: string[];
    latest_images?: string[];
    versions?: Record<string, string>;
    application: string;
    branch: string;
  } | null;
}

/** App running in baseline with no feature-branch changes but sharing an infra dependency. */
export interface BaselineCandidate {
  application: string;
  currentImage: string | null;
  workloadType?: 'deployment' | 'statefulset';
  isRunning?: boolean;
  envVarNames?: string[];
  /** Values of DATABASE/DB_NAME/DBNAME/DB/SCHEMA-suffixed env vars, captured live from the running
   *  pod — keyed by env var name, used to prefill the DB Branching "Database Name" field. */
  dbSchemaNames?: Record<string, string>;
  /** appmetadata.metadata.db_branches for this service, if any — see DbBranchAppMetaOverride. */
  dbBranchOverrides?: Partial<Record<DbBranchUiType, DbBranchAppMetaOverride>>;
  /** Pre-populated Kafka topic catalog for this service, if any — see KafkaTopicCatalogEntry. */
  kafkaTopics?: KafkaTopicCatalogEntry[];
  /** Hardcoded env var defaults from mirrord-preview.tmpl's feature.default_env, if any. */
  defaultEnv?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// mirrord per-service override types
// ---------------------------------------------------------------------------
import { DB_TYPE_TO_MIRRORD, type DbBranchUiType } from '../../../../shared/previewEnv/matchEnvVarsToDbFields';
export type { DbBranchUiType };
export { DB_TYPE_TO_MIRRORD };

/**
 * A DB connection param can be:
 *   - a plain env var name string
 *   - a list of candidate env var names — the operator tries each in order and uses whichever
 *     actually exists on the target pod. Confirmed live 2026-10-23 against operator 3.221.0
 *     (a deliberately-first bogus candidate still resolved correctly via the second entry).
 *   - a pattern-extraction object, where the value is extracted from another env var
 *     (e.g. a JDBC URL) via a named-capture regex. Confirmed working live 2026-07-31 against
 *     mirrord operator 3.188.0 / CLI 3.240.0 (host/port/database all extract and connect
 *     correctly under copy.mode all and schema) — earlier versions failed to parse this shape
 *     client-side entirely (see metalbear_support.md).
 */
export type DbBranchParamValue =
  | string
  | string[]
  | { env_var_name: string; value_pattern: string };

/**
 * appmetadata.metadata.db_branches.<type> shape (see application-metadata-schema.yaml) — per-type
 * overrides for services whose env var naming doesn't match auto-detection. Used as a fallback:
 * live env-var matching is tried first, and any field it can't resolve falls back to this.
 */
export interface DbBranchAppMetaOverride {
  schema_name?: string;
  version?: string;
  /** Selects a cluster-admin-defined branch config profile (image/registry/server-args preset) for
   *  this db type, mirroring mirrord's `profile` field. Only meaningful once the operator/CLI
   *  versions supporting Branch Config Profiles are deployed — see metalbear_redis_branch_image_and_flags.md. */
  profile?: string;
  env_var_overrides?: Partial<Record<string, DbBranchParamValue>>;
  /** Mirrors the template's own `db_branches[].copy.mode`, if set — lets a repo's
   *  mirrord-preview.tmpl override the wizard's type-based copy.mode default (e.g. a CCM app's
   *  plain redis explicitly wanting "all" despite redis defaulting to "empty"). */
  copy_mode?: string;
  /** Mirrors the template's own `db_branches[].image` — a full registry/repo path, optionally
   *  containing the literal token `$RANCH_INFERRED_VERSION`, resolved against the live dependency
   *  workload's own image tag the same way `version` already is. Mutually exclusive with `version`. */
  image?: string;
  /** Mirrors the template's own `db_branches[].copy.dump_args` — extra flags passed to the source
   *  dump tool (e.g. mysqldump's `--single-transaction`), unrelated to copy.mode so tracked separately. */
  dump_args?: string[];
  /** Mirrors the template's own `db_branches[].creation_timeout_secs` — how long mirrord waits for
   *  the branch pod to become ready before giving up. Lets a template raise mirrord's 60s default
   *  (and this library's own 600s default) for slow `copy.mode: all` imports. */
  creation_timeout_secs?: number;
  /** Mirrors the template's own `db_branches[].ttl_secs` — operator-side reap timer for the branch. */
  ttl_secs?: number;
}

export interface MigrationsContainer {
  flavor: 'container';
  /** Full image reference for the migration container, including the tag. */
  image: string;
  /** Entrypoint command override. When unset, the image's own entrypoint runs. */
  command?: string[];
  /** Entrypoint args override. */
  args?: string[];
  /** Extra environment variables for the container — literal key/value pairs only, no envFrom. */
  env?: Record<string, string>;
}

export type MigrationsConfig = MigrationsContainer;

export interface DbBranchEntry {
  id: string;
  type: DbBranchUiType;
  version?: string;
  /** Full registry/repo path for the branch database container. Mutually exclusive with `version`. */
  image?: string;
  name?: string;
  /** Cluster-admin-defined branch config profile name (image/registry/server-args preset). */
  profile?: string;
  ttl_secs?: number;
  creation_timeout_secs?: number;
  /**
   * UI stores all DB connection fields under `connection.params`.
   * Each value is either a plain env var name string or a `{ env_var_name, value_pattern }` object
   * for extracting a field from a composite env var (e.g. a JDBC URL) via named-capture regex.
   */
  connection?: {
    params?: Record<string, DbBranchParamValue>;
  };
  /** `tables` lets specific tables opt back into a lighter copy mode. `dump_args` passes extra
   *  flags to the source dump tool (e.g. mysqldump's `--single-transaction`). */
  copy?: { mode: string; tables?: Record<string, { filter?: string }>; dump_args?: string[] };
  /**
   * Runs `rake db:migrate` (or equivalent) against the branch after it's created — only used for
   * Rails/rake `container`-flavor apps. Postgres/Flyway apps never need this: Spring Boot's
   * embedded flyway-core auto-runs their migrations on boot once copy.mode is "all".
   */
  migrations?: MigrationsConfig;
}

export interface KafkaSplitEntry {
  queue_type: 'Kafka';
  message_filter?: { baggage?: string };
  /** Env var in the deployment that holds the topic name (for MirrordSplitConfig). Optional — falls back to envLike matching. */
  topic_env_var?: string;
  /** Env var in the deployment that holds the consumer group ID. Optional — falls back to envLike matching. */
  group_id_env_var?: string;
}

export interface MirrordOverrides {
  feature?: {
    env?: { override?: Record<string, string> };
    db_branches?: DbBranchEntry[];
    split_queues?: Record<string, KafkaSplitEntry>;
  };
}

export interface PreviewService {
  name: string;
  image: string;
  type?: 'deployment' | 'statefulset' | 'rollout';
  /** Optional per-service mirrord overrides — deep-merged onto library base in Jenkinsfile */
  mirrordOverrides?: MirrordOverrides;
  /** Topic catalog for ensureKafkaTopicCatalog() (DEVEX-819), copied from its candidate. */
  kafkaTopicsCatalog?: KafkaTopicCatalogEntry[];
}

export interface ServiceDependency {
  name: string;
  required: boolean;
}

export interface InfraService {
  name: string;
  type: 'deployment' | 'statefulset';
  currentImage: string | null;
  isRunning: boolean;
  images?: {
    image_name?: string;
    deploy_tags?: string[];
    found_tags?: string[];
    versions?: Record<string, string>;
    application: string;
    branch: string;
  } | null;
}

export interface BaselineNamespace {
  name: string;
  status: string;
  class?: string;
  params?: Record<string, any>;
}

export interface PRResolution {
  repo: string;
  branch: string;
  title: string;
  state: string;
  number: number;
  url: string;
}

export async function apiBaselineNamespaces(
  tokenGetter: () => Promise<string>,
): Promise<BaselineNamespace[]> {
  const data = await fetchApi<{ namespaces: BaselineNamespace[] }>(
    withCluster('/api/namespaces.json?filter=baseline', globalThis.cluster_selected_value),
    tokenGetter,
  );
  return data.namespaces || [];
}

export async function apiOverlayCandidates(
  branches: string[],
  namespace: string,
  tokenGetter: () => Promise<string>,
  dbBranch?: string,
  cluster?: string,
  prUrls?: string[],
  onlyPr?: boolean,
): Promise<{ candidates: OverlayCandidate[]; baselineCandidates: BaselineCandidate[]; infraServices: InfraService[]; infraDepsMap: Record<string, string[]>; serviceDepsMap: Record<string, ServiceDependency[]>; suggestedServices: string[]; errors?: string[] }> {
  const qs = new URLSearchParams({ branches: branches.join(','), namespace });
  if (dbBranch && !branches.includes(dbBranch)) qs.set('dbBranch', dbBranch);
  if (prUrls?.length) qs.set('prUrls', prUrls.join(','));
  if (onlyPr) qs.set('onlyPr', 'true');
  const path = withCluster(`/api/preview/overlay-candidates?${qs}`, cluster ?? globalThis.cluster_selected_value);
  const data = await fetchApi<{ candidates: OverlayCandidate[]; baselineCandidates?: BaselineCandidate[]; infraServices?: InfraService[]; infraDepsMap?: Record<string, string[]>; serviceDepsMap?: Record<string, ServiceDependency[]>; suggestedServices?: string[]; errors?: string[] }>(
    path,
    tokenGetter,
  );
  return {
    candidates: data.candidates || [],
    baselineCandidates: data.baselineCandidates || [],
    infraServices: data.infraServices || [],
    infraDepsMap: data.infraDepsMap || {},
    serviceDepsMap: data.serviceDepsMap || {},
    suggestedServices: data.suggestedServices || [],
    ...(data.errors?.length ? { errors: data.errors } : {}),
  };
}

export async function apiSessionInUse(
  namespace: string,
  previewTag: string,
  tokenGetter: () => Promise<string>,
): Promise<boolean> {
  const qs = new URLSearchParams({ namespace, previewTag });
  const data = await fetchApi<{ inUse: boolean }>(
    `/api/preview/session-in-use?${qs}`,
    tokenGetter,
  );
  return data.inUse ?? false;
}

/**
 * A migrations template returned by /api/preview/migrations-check — the flavor/path or
 * flavor/command shape to attach automatically. `container` templates deliberately omit
 * `image` (the client fills it in with the service's own resolved deploy image) and `env`
 * (left for manual entry via the UI — see ServiceOverridesPanel's Schema Migrations env
 * editor, since auto-populating a real pod's full env risks leaking secrets — undecided,
 * see db-branching-env-var-audit.md finding #7 for the open security question).
 */
export type MigrationsTemplate = { flavor: 'container'; command: string[] };

/**
 * Checks whether a service's feature branch touched its migration files, and if so returns
 * the migrations config template to attach automatically — there is no manual UI control for
 * the flavor/image/command; only the `container` flavor's env vars are user-editable. Migrations
 * coverage now comes from each repo's own mirrord-preview.tmpl, not a central profile list.
 */
export async function apiMigrationsCheck(
  service: string,
  branch: string,
  tokenGetter: () => Promise<string>,
): Promise<{ applicable: boolean; template?: MigrationsTemplate }> {
  const qs = new URLSearchParams({ service, branch });
  try {
    return await fetchApi<{ applicable: boolean; template?: MigrationsTemplate }>(
      `/api/preview/migrations-check?${qs}`,
      tokenGetter,
    );
  } catch {
    return { applicable: false };
  }
}

export async function apiPreviewDeploy(
  payload: {
    environment: string;
    namespace: string;
    previewKey: string;
    ttl: string;
    services: PreviewService[];
    infra?: PreviewService[];
    dbBranch?: string;
    force?: boolean;
    /** Every companion deployment name (e.g. tas-sidekiq, tas-karafka) the wizard surfaced as a
     *  selectable candidate for the services in this request — present (even empty) whenever the
     *  client is companion-aware, so the server can tell a deliberate deselection apart from an
     *  old/non-wizard client that doesn't know companions exist at all. See COMPANION_DEPLOYMENTS
     *  in routes/previewenv.ts. */
    companionsConsidered?: string[];
  },
  tokenGetter: () => Promise<string>,
): Promise<{ status: 'triggered'; jobUrl: string } | { status: 'queued'; id: number }> {
  const normalized = {
    ...payload,
    services: normalizeDbBranchTypes(payload.services),
    ...(payload.infra ? { infra: normalizeDbBranchTypes(payload.infra) } : {}),
  };
  return fetchApi(
    '/api/preview/deploy',
    tokenGetter,
    { method: 'POST', body: JSON.stringify(normalized) },
  );
}

/** Normalize user-facing DB type labels to mirrord enum values.
 *  Also strips empty-string `name` values — Redis interprets `name` as a numeric DB
 *  index and mirrord will error on `""` with "cannot parse integer from empty string".
 */
function normalizeDbBranchTypes(services: PreviewService[]): PreviewService[] {
  return services.map((svc) => {
    const branches = svc.mirrordOverrides?.feature?.db_branches;
    if (!branches?.length) return svc;
    return {
      ...svc,
      mirrordOverrides: {
        ...svc.mirrordOverrides,
        feature: {
          ...svc.mirrordOverrides!.feature,
          db_branches: branches.map((b) => {
            const isRedis = b.type === 'redis';
            const entry: DbBranchEntry = {
              ...b,
              type: (DB_TYPE_TO_MIRRORD[b.type] ?? b.type) as DbBranchEntry['type'],
            };

            // Strip empty name so mirrord doesn't receive "" where it expects an integer (Redis)
            // or an omitted optional field (other types).
            if (!entry.name) delete entry.name;
            // Same for profile — an unset UI field defaults to "", which isn't a valid profile name.
            if (!entry.profile) delete entry.profile;

            if (isRedis) {
              // Redis uses connection.params just like MySQL/Postgres/Mongo — the mirrord CLI
              // only accepts the params wrapper format for Redis db_branches at this version.
              // The flat {host, port} format and {type:"env",variable} form both fail schema
              // validation at the CLI level.
              //
              // `copy` is intentionally KEPT for redis (previously stripped here) — redis's own
              // default copy mode is "empty" (a blank instance, no data at all) unless explicitly
              // set to "all", confirmed live 2026-08-05. Stripping it here silently reverted every
              // redis branch to an empty copy regardless of what ServiceOverridesPanel's toggle set.
            }

            return entry;
          }),
        },
      },
    };
  });
}

// Must match the `key`s in REGRESSION_TEST_SUITES (web/routes/previewenv.ts) — that config is the
// single source of truth for which suites exist and what params each one sends to Jenkins.
export type RegressionTestSuite = 'web_oneapp' | 'web_non_oneapp' | 'ios' | 'api' | 'android';

export interface RegressionTestSuiteSchema {
  key: RegressionTestSuite;
  label: string;
  platform: 'web' | 'api' | 'ios' | 'android';
  fields: ParamFieldSchema[];
  defaults: Record<string, string>;
}

/** Fetches, per suite, which Jenkins params can be overridden (with their type/choices) and
 *  today's curated default value for each — drives the expandable per-suite override form. */
export async function apiRegressionTestSchema(
  tokenGetter: () => Promise<string>,
): Promise<RegressionTestSuiteSchema[]> {
  const data = await fetchApi<{ suites: RegressionTestSuiteSchema[] }>(
    '/api/preview/regression-test/schema',
    tokenGetter,
  );
  return data.suites || [];
}

/** Triggers one of the e2e regression-suite Jenkins jobs against a just-deployed preview
 *  session. Fire-and-forget — the response is just the triggered build's URL to link out to,
 *  Ranch does not poll these suites for status. `paramOverrides` only needs to carry fields the
 *  user actually edited away from that suite's defaults; the backend re-applies its own defaults
 *  first, so an omitted field always resolves to the curated default, never the Jenkinsfile's own
 *  bare fallback. */
export async function apiTriggerRegressionTest(
  payload: {
    suite: RegressionTestSuite;
    namespace: string;
    environment: string;
    previewKey: string;
    services: string[];
    paramOverrides?: Record<string, string>;
  },
  tokenGetter: () => Promise<string>,
): Promise<{ status: 'triggered'; jobUrl: string } | { status: 'queued'; id: number }> {
  return fetchApi(
    '/api/preview/regression-test',
    tokenGetter,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export interface SessionStatusRecord {
  status: 'pending' | 'running' | 'failed';
  triggeredByName: string;
  triggeredByEmail: string;
  ttlExpiresAt: string;
}

export async function apiSessionStatus(
  namespace: string,
  previewTag: string,
  tokenGetter: () => Promise<string>,
): Promise<SessionStatusRecord | null> {
  const qs = new URLSearchParams({ namespace, previewTag });
  const data = await fetchApi<{ session: SessionStatusRecord | null }>(
    withCluster(`/api/preview/session-status?${qs}`, globalThis.cluster_selected_value),
    tokenGetter,
  );
  return data.session ?? null;
}

export async function apiJenkinsBuildStatus(
  jobUrl: string,
  tokenGetter: () => Promise<string>,
): Promise<{ building: boolean; result: string | null }> {
  const qs = new URLSearchParams({ jobUrl });
  return fetchApi<{ building: boolean; result: string | null }>(
    `/api/preview/jenkins-build-status?${qs}`,
    tokenGetter,
  );
}

export async function apiConfirmSession(
  namespace: string,
  previewTag: string,
  ttl: string,
  tokenGetter: () => Promise<string>,
): Promise<void> {
  await fetchApi(
    '/api/preview/confirm',
    tokenGetter,
    { method: 'POST', body: JSON.stringify({ namespace, previewTag, ttl }) },
  );
}

export async function apiStopPreview(
  namespace: string,
  previewTag: string,
  tokenGetter: () => Promise<string>,
): Promise<void> {
  await fetchApi(
    '/api/preview/stop',
    tokenGetter,
    { method: 'POST', body: JSON.stringify({ namespace, previewTag, environment: globalThis.cluster_selected_value }) },
  );
}

// 404s on clusters where ENABLE_PREVIEW_ENDPOINT isn't set — treat that (and any other error) as unavailable.
export async function apiPreviewStopAvailable(tokenGetter: () => Promise<string>): Promise<boolean> {
  try {
    const qs = new URLSearchParams({ environment: String(globalThis.cluster_selected_value ?? '') });
    const result = await fetchApi<{ available: boolean }>(`/api/preview/stop-available?${qs}`, tokenGetter);
    return result.available === true;
  } catch {
    return false;
  }
}

/**
 * Resolve a full GitHub PR URL to its head branch.
 * URL format: https://github.com/<org>/<repo>/pull/<number>
 */
export async function apiResolvePR(
  prUrl: string,
  tokenGetter: () => Promise<string>,
): Promise<PRResolution> {
  const qs = new URLSearchParams({ url: prUrl });
  return fetchApi<PRResolution>(
    `/api/git/pr-branch?${qs}`,
    tokenGetter,
  );
}
